import { Box, ColumnConfig, DataTable, Heading, Text } from "grommet";
import { flatten, uniq, orderBy, get } from "lodash";
import React from "react";
import { useParams } from "react-router-dom";
import LiveHub from "../general/LiveHub";
import { StatIcon } from "../stats/StatIcon";
import { StatValue } from "../stats/StatRenderer";
import { getStats, StatViewModel } from "../stats/StatsStore";
import { getLeague, LeagueViewModel } from "./LeaguesStore";
import { TeamStatisticsViewModel } from "../seasons/SeasonsStore";

export const LeagueTableWrapper = () => {
    let { id } = useParams<{ id: string }>();
    const [league, setLeague] = React.useState({} as LeagueViewModel);

    React.useEffect(() => {
        resolve();
    }, []);

    const resolve = async () => {
        let statsResult = await getStats();
        let league = await getLeague(id);
        setLeague(league);
        setStatTypes(statsResult.result);
    };

    const [statTypes, setStatTypes] = React.useState([] as StatViewModel[]);

    React.useEffect(() => {}, []);

    return (
        <LiveHub entities={[id]} handleUpdate={resolve}>
            <Box fill pad="medium">
                <Heading>{league.name}</Heading>
                <LeagueTable league={league} statTypes={statTypes}></LeagueTable>
            </Box>
        </LiveHub>
    );
};

const LeagueTable = ({ league, statTypes }: { league: LeagueViewModel; statTypes: StatViewModel[] }) => {
    const [statColumns, setStatColumns] = React.useState([] as any[]);
    const [sortedTeamStats, setSortedTeamStats] = React.useState([] as TeamStatisticsViewModel[]);

    React.useEffect(() => {
        if (league.id && statTypes) {
            let tempColumns = [] as string[];
            let allStats = flatten(league.teamStatistics?.map((teamStats) => teamStats.statistics));

            allStats.forEach((stat) => {
                Object.entries(stat).forEach(([key, value]) => {
                    if (!["wins", "losses", "draws"].includes(key)) {
                        tempColumns.push(key);
                    }
                });
            });

            let stats =
                uniq(tempColumns).map((col) => {
                    let stat = statTypes.find((s) => s.id == col) || ({} as StatViewModel);
                    return {
                        property: "statistics." + col,
                        header: (
                            <Text>
                                {StatIcon(stat)} {stat?.name}
                            </Text>
                        ),
                        sortable: true,
                        aggregate: "sum",
                        footer: { aggregate: true },
                        render: (teamStat: TeamStatisticsViewModel) => <Text>{StatValue(stat, teamStat.statistics[col])}</Text>,
                    } as ColumnConfig<TeamStatisticsViewModel>;
                }) || [];

            if (allStats.filter((s) => s["losses"] != undefined).length > 0) {
                stats.push({ property: "statistics.losses", header: "Losses", sortable: true, aggregate: "sum", footer: { aggregate: true } });
                league.teamStatistics?.forEach((teamStat) => {
                    if (teamStat.statistics["losses"] == undefined) {
                        teamStat.statistics["losses"] = 0;
                    }
                });
            }

            if (allStats.filter((s) => s["draws"] != undefined).length > 0) {
                stats.push({ property: "statistics.draws", header: "Draws", sortable: true, aggregate: "sum", footer: { aggregate: true } });
                league.teamStatistics?.forEach((teamStat) => {
                    if (teamStat.statistics["draws"] == undefined) {
                        teamStat.statistics["draws"] = 0;
                    }
                });
            }

            league.teamStatistics?.forEach((teamStat) => {
                tempColumns.forEach((key) => {
                    if (teamStat.statistics[key] == undefined) {
                        teamStat.statistics[key] = 0;
                    }
                });
            });

            setStatColumns(stats);

            setSortedTeamStats(orderBy(league.teamStatistics, ["statistics.wins"], ["desc"]));
        }
    }, [league, statTypes]);

    const [sortProperty, setSortProperty] = React.useState("statistics.wins");
    const [sortDirection, setSortDirection] = React.useState("desc" as "asc" | "desc");

    const onTableSort = ({ property, direction }) => {
        if (property != sortProperty) {
            direction = "desc";
        }
        
        let stat = statTypes.find((s) => s.id == property.replace("statistics.", ""));
        if (stat?.type == "Time" && direction == "asc") {
            setSortedTeamStats(
                orderBy(
                    sortedTeamStats,
                    [
                        (s) => {
                            let value = get(s, property);
                            if (value == 0) {
                                if (direction != "asc") {
                                    return Number.MAX_VALUE;
                                } else {
                                    return Number.MIN_VALUE;
                                }
                            }
                            return value;
                        },
                    ],
                    [direction]
                )
            );
        } else {
            setSortedTeamStats(orderBy(sortedTeamStats, [property], [direction]));
        }
        setSortProperty(property);
        setSortDirection(direction);
    };

    return (
        <DataTable data={sortedTeamStats} columns={[{ property: "team.name", header: "Team", primary: true, sortable: true, footer: "Total" }, { property: "statistics.wins", header: "Wins", sortable: true, aggregate: "sum", footer: { aggregate: true } }, ...statColumns]} sort={{ property: sortProperty, direction: sortDirection, external: true }} sortable={true} onSort={onTableSort}></DataTable>
    );
};

export default LeagueTable;
